import axios from 'axios';

export const REFERRAL_REALTIME_UPDATES_FEATURE = 'referral_realtime_updates';
export const REFERRAL_ENTITY_LINKING_FEATURE = 'referral_entity_linking';
export const REFERRAL_REPLY_DEPARTMENT_FILTER_FEATURE = 'referral_reply_department_filter';
export const CREATE_TASK_CHART_CLARITY_FEATURE = 'create_task_chart_clarity';
const SEND_TO_CHART_TOOLTIP = 'Files this document in the patient\'s chart';
const CREATE_TASK_TOOLTIP = 'Creates an EMR task \u2014 does not file the document in the chart';
// Gates the silent background inbox refresh after record actions (archive,
// notify, send-to-chart). Off = legacy behavior: full page reloads and
// foreground refetches. Per-org so orgs with slow inbox queries can be opted
// in for testing (or back out) independently.
export const INBOX_BACKGROUND_REFRESH_FEATURE = 'inbox_background_refresh';
// Gates SMS template variable substitution ({{first_name}}, {{referring_provider}})
// in the send-SMS modal and the greeting-suppression rule. Off = legacy behavior:
// the raw template message is always prefixed with "Hello[ FirstName], ".
export const SMS_TEMPLATE_VARIABLES_FEATURE = 'sms_template_variables';

/**
 * Fetches feature flags from GET - api/v1/features
 */
export const fetchFeatureFlags = () => axios.get('/api/v1/features');

/**
 * Saves feature flags in local storage
 */
export const saveFeatureFlags = (flags) => {
  try {
    const serializedData = JSON.stringify(flags);
    localStorage.setItem('flags', serializedData);
  } catch {
    return [];
  }
};

export const clearFeatureFlags = () => {
  try {
    localStorage.removeItem('flags');
  } catch {
    return false;
  }
  return true;
};

/**
 * Saves user flags in local storage
 */
export const saveUserFlags = (userFlags) => {
  try {
    const serializedData = JSON.stringify(userFlags);
    localStorage.setItem('user_flags', serializedData);
  } catch {
    return [];
  }
};

/**
 * Checks if local storage has the feature flag in question.
 * Handles both array format and response-shaped format { data: { flags: [...] } }
 */
export const hasFeatureInLocalStorage = (featureName) => {
  try {
    const serializedData = localStorage.getItem('flags');
    const featureFlags = JSON.parse(serializedData);
    if (featureFlags === null) {
      return false;
    }

    // Normalize to array - handle both direct array and response-shaped object
    let flagsArray;
    if (Array.isArray(featureFlags)) {
      flagsArray = featureFlags;
    } else if (Array.isArray(featureFlags?.data?.flags)) {
      flagsArray = featureFlags.data.flags;
    } else {
      return false;
    }

    const [feature] = flagsArray.filter((flag) => flag.name === featureName);
    return feature?.active || false;
  } catch {
    return false;
  }
};

const normalizeFeaturePayload = (featureFlags) => {
  if (Array.isArray(featureFlags)) {
    return { flags: featureFlags };
  }
  if (Array.isArray(featureFlags?.data?.flags)) {
    return featureFlags.data;
  }
  if (Array.isArray(featureFlags?.flags)) {
    return featureFlags;
  }

  return null;
};

export const chartClarityTooltips = (autoUploadEmr) => {
  if (!hasFeatureInLocalStorage(CREATE_TASK_CHART_CLARITY_FEATURE)) {
    return { sendToChart: undefined, createTask: undefined };
  }

  return {
    sendToChart: SEND_TO_CHART_TOOLTIP,
    createTask: autoUploadEmr === 'Kareo' ? undefined : CREATE_TASK_TOOLTIP,
  };
};

/**
 * Loads feature flags from feature flags
 * Fetches from backend if local storage is empty
 */
export const loadFeatureFlagPayload = async () => {
  try {
    const serializedData = localStorage.getItem('flags');
    const featureFlags = JSON.parse(serializedData);
    const payload = normalizeFeaturePayload(featureFlags);
    if (!payload) {
      throw new Error('Flags is not an array or array is empty');
    }
    return payload;
  } catch {
    const featureFlags = await fetchFeatureFlags();
    saveFeatureFlags(featureFlags);
    return featureFlags.data;
  }
};

export const loadFeatureFlags = async () => {
  const payload = await loadFeatureFlagPayload();
  return payload.flags || [];
};

export const loadFeatureSettings = async (featureName) => {
  let payload = await loadFeatureFlagPayload();
  if (!payload.feature_settings) {
    const featureFlags = await fetchFeatureFlags();
    saveFeatureFlags(featureFlags);
    payload = featureFlags.data;
  }
  return payload.feature_settings?.[featureName] || {};
};

/**
 * Takes in the feature name and verifies,
 * if user has access to this feature.
 * @param {String} feature_name
 */
export const verifyFeatureAccess = async (featureName) => {
  const featureFlags = await loadFeatureFlags();
  const [feature] = featureFlags.filter((flag) => flag.name === featureName);
  if (feature && feature.active) return feature.active;
  return false;
};

/**
 * Verifies if user has access to a feature.
 * @param {String} featureName
 */
export const verifyReferralMgmtAccess = async () => {
  const referralManagementStatus = localStorage.getItem('m8_referral_management');
  return referralManagementStatus === 'true';
};

export const verifyAnalyticsAccess = async () => {
  const analyticsAccess = localStorage.getItem('m8_view_referral_analytics');
  return analyticsAccess === 'true';
};

export const verifyReceivedInboxAccess = async () => {
  const receivedInboxAccess = localStorage.getItem('m8_access_received_inbox');
  return receivedInboxAccess === 'true';
};

export const verifySentInboxAccess = async () => {
  const sentInboxAccess = localStorage.getItem('m8_access_sent_inbox');
  return sentInboxAccess === 'true';
};

export const verifyVoiceInboxAccess = async () => {
  const voiceInboxAccess = localStorage.getItem('m8_access_voice_inbox');
  return voiceInboxAccess === 'true';
};

export const verifyPatientRecaptureAccess = async () => {
  const patientRecaptureAccess = localStorage.getItem('m8_patient_recapture');
  return patientRecaptureAccess === 'true';
};

export const verifyReferralManagementAccess = async () => {
  const referralManagementAccess = localStorage.getItem('m8_referral_management');
  return referralManagementAccess === 'true';
};
